<template>

  <NdsDlg
      v-if="selectedApplic"
      :isOpen.sync="isNdsDlgOpen"
      :application="selectedApplic"
      @update:isOpen="val => {
      isNdsDlgOpen = val;
      if (!val) selectedApplic = null;
    }"
  />

  <Card class="winners-card-wrapper" rounded="lg">
    <div class="d-flex justify-space-between align-center">
      <div class="cardTitle">
        {{ $t(winners.length === 1 ? 'Информация о победителе' : 'Информация о победителях') }}
      </div>
      <v-spacer />
      <v-switch
        v-if="withPositions && isAnyNds"
        :label="$t('Показать цены без НДС')"
        hide-details
        inset
        class="mr-4"
        :model-value="isEnabled"
        @update:model-value="val => isEnabled = val"
      ></v-switch>
    </div>

    <div
      v-if="!withPositions || winnersInfoStore.needOfferPrice"
      class="d-flex flex-column"
    >
      <div
        v-for="(winner, index) in winners" :key="index"
        class="winner-item active-panel d-flex justify-space-between px-5 py-6 align-center"
      >
        <div class="d-flex align-center ga-1">
          <span>{{ winner.organizationName }}</span>
          <v-icon size="22" color="blue">mdi-check-decagram</v-icon>
        </div>
        <span
          :style="{ cursor: winner.isNds ? 'pointer' : 'default' }"
          @click="winner.isNds ? openDialog(winner) : null"
        >
          {{ formatMoneyText(calculateApplicationCost(winner)) }}
        </span>
      </div>
      <v-btn
        v-if="procedureStepCode === procedureStep.COMPLETED"
        color="primary"
        density="comfortable"
        variant="outlined"
        class="mt-6"
        style="width: fit-content"
        @click="downloadResults()"
      >
        <v-icon class="mr-1">custom:downloadFileIcon</v-icon>{{ $t('Скачать результаты') }}</v-btn>
    </div>
    <div v-else>
      <v-expansion-panels
        v-model="panel"
        flat
      >
        <template v-for="(winner, index) in winners" :key="index">
          <v-expansion-panel>
            <template #title>
              <div class="winner-item d-flex justify-space-between w-100 align-center">
                <div class="d-flex align-center ga-1">
                  <span>{{ winner.organizationName }}</span>
                  <v-icon size="22" color="blue">mdi-check-decagram</v-icon>
                </div>
                <span
                  style="z-index: 2"
                  :style="{ cursor: winner.isNds ? 'pointer' : 'default' }"
                  @click.stop="winner.isNds ? openDialog(winner) : null"
                >
                  {{ formatMoneyText(getTotalSum(winner)) }}
                </span>
              </div>
            </template>

            <v-expansion-panel-text>
              <div
                v-for="(position, posIndex) in winner.positions"
                :key="posIndex"
                class="d-flex justify-space-between px-5 py-6 align-center"
                :class="{ 'border-bottom': posIndex !== winner.positions.length - 1 }"
              >
                <div class="d-flex align-center ga-3 w-50">
                  <div class="position-long-name">
                    {{ position.name }}
                    <v-tooltip
                      activator="parent"
                      location="top"
                    >
                      {{ position.name }}
                    </v-tooltip>
                  </div>
                  <span class="quantity-round">
                    {{ `${position.quantity} ${position.measure}` }}
                  </span>
                </div>
                <span
                  class="price"
                  :style="{ cursor: winner.isNds ? 'pointer' : 'default' }"
                  @click="winner.isNds ? openDialog(winner, position) : null"
                >
                  {{ formatMoneyText(calculatePositionCost(position, winner.ndsValue, winnersInfoStore.showPriceInsteadOfCost ? 1 : position.quantity)) }}
                </span>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </template>
      </v-expansion-panels>
      <v-row
        class="mt-6"
        :class="{
          'justify-space-between' : procedureStepCode === procedureStep.COMPLETED,
          'justify-end' : procedureStepCode !== procedureStep.COMPLETED
        }"
        no-gutters
      >
        <v-btn
          v-if="procedureStepCode === procedureStep.COMPLETED"
          color="primary"
          density="comfortable"
          variant="outlined"
          @click="downloadResults()"
        >
          <v-icon class="mr-1">custom:downloadFileIcon</v-icon>{{ $t('Скачать результаты') }}</v-btn>
        <div class="total-sum-text">{{ $t('Итоговая сумма заказа:') }}<span class="total-sum-price">{{ formatStartPrice(getTotalSum()) + ' $ ' }}</span>
        </div>
      </v-row>
    </div>
  </Card>
</template>

<script>
import Card from "@intec/vue-components/form/Card.vue";
import procedureStep from "@/components/ProcedureStep";
import { formatMoneyText, formatStartPrice } from '@/filters';
import {  getIsNdsText } from "@/lib/nds";
import { useLibraryStore } from "@/stores/library";
import { useWinnersInfoStore } from "@/stores/winnersInfoStore";
import { onMounted } from "vue";
import NdsDlg from "@/components/application/dialogs/NdsDlg.vue";
import { useApplicationCalculator } from '@/lib/application';
import { useApplicationPositionCalculator } from '@/lib/applicationPosition';
import Decimal from "decimal.js-light";

export default {
  name: 'WinnersInfo',
  components: {NdsDlg, Card },
  props: {
    winners: {
      type: Array,
      required: true,
    },
    withPositions: {
      type: Boolean,
      default: false,
    },
    procedureStepCode: {
      type: String
    },
    procedure: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      formatMoneyText,
      procedureStep,
      panel: undefined,
      selectedApplic: null,
      isNdsDlgOpen: false,
      isEnabled: false
    }
  },
  computed: {
    isAnyNds() {
      const winnerWithNds = this.winners.find(winner => winner.ndsValue !== null);
      return winnerWithNds ? winnerWithNds.ndsValue : null;
    }
  },
  setup(props) {
    const winnersInfoStore = useWinnersInfoStore();
    const libraryStore = useLibraryStore();

    onMounted(() => {
      libraryStore.callActionByCode('initWinnersInfoOptions', {
        procedureType: props.procedure.type
      });
    });

    return {
      winnersInfoStore,
      libraryStore
    };
  },
  methods: {
    getIsNdsText,
    formatStartPrice,
    async downloadResults() {
      // Пытаемся получить ссылку от внешнего модуля.
      const overrideLink = this.libraryStore.callFunctionByCode('getLinkDownloadResults', {
        procedure: this.procedure
      }).linkDownloadResults ?? null;

      // Если модуль вернул строку — используем её, иначе дефолтную.
      window.location.href = typeof overrideLink === 'string'
        ? overrideLink
        : `/procedures/${this.procedure.id}/resultsExport`;
    },
    calculatePositionCost(position, ndsRate, quantity) {
      if (this.winnersInfoStore.customFieldInsteadOfCost && position[this.winnersInfoStore.customFieldInsteadOfCost]) {
        return position[this.winnersInfoStore.customFieldInsteadOfCost];
      }
      const calculator = useApplicationPositionCalculator(position, ndsRate);
      if (!quantity) {
        return calculator.getCostForQuantity(1);
      }
      return this.isEnabled  ? calculator.getCostForQuantity(quantity) : calculator.getCostNdsForQuantity(quantity);
    },
    calculateApplicationCost(application) {
      const calculator = useApplicationCalculator(
          application,
          application.positions,
          application.ndsValue,
          this.winnersInfoStore.customFieldInsteadOfCost
      );
      return this.isEnabled ? calculator.getChosenCost() : calculator.getChosenCostNds();
    },
    getTotalSum(winner = null) {
      if (winner) {
        if (!winner.positions) return 0;

        return this.calculateApplicationCost(winner);
      }

      // Общая сумма по всем победителям
      return this.winners.reduce((total, winner) => {
        if (!winner.positions) return total;

        return total.plus(new Decimal(this.calculateApplicationCost(winner)));
      }, new Decimal(0)).toString();
    },
    openDialog(winner, position = null) {
      this.selectedApplic = {
        offerPrice: winner.offerPrice,
        positions: position ? [position] : winner.positions,
        nds: {
          rate: winner.ndsValue
        }
      };
      this.isNdsDlgOpen = true;
    }
  }
};
</script>

<style scoped lang="scss">
.position-long-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.winners-card-wrapper {
  font-weight: 500;
  font-size: 16px;

  .company-link {
    color: unset;
  }

  .winner-item {
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    font-size: 20px;
  }

  .active-panel {
    cursor: default;
  }

  .winner-item:hover,
  .active-panel,
  :deep(.v-expansion-panel-title:hover),
  :deep(.v-expansion-panel-title--active) {
    background-color: rgb(var(--grey-lighten-color));
  }

  .quantity-round {
    font-family: var(--primary-font-family-name);
    border-radius: 25px;
    padding: 4px 20px;
    color: rgb(var(--intec-primary-color));
    background-color: rgb(var(--quantity-round));
  }

  .border-bottom {
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  .total-sum {
    &-text {
      font-weight: 400;
      font-size: 16px;
      color: rgba(var(--black-color), 0.6);
    }

    &-price {
      font-weight: 500;
      font-size: 20px;
      color: rgb(var(--black-color));
    }
  }

  :deep(.v-expansion-panel-text__wrapper) {
    padding: 0;
  }

  :deep(.v-expansion-panel-title) {
    padding: 24px 20px;
  }
}

/* v-switch */
:deep(.v-input--density-default) {
  --v-input-control-height: 28px;
}

:deep(.v-selection-control--density-default) {
  --v-selection-control-size: 28px;
}

:deep(.v-switch) {
  margin-left: auto;
}

:deep(.v-switch .v-label) {
  color: rgb(var(--v-theme-primary));
  opacity: 1;
  font-family: Abrade;
  font-weight: 500;
  font-size: 16px;
}

:deep(.v-switch .v-selection-control) {
  max-height: var(--v-input-control-height);
  flex-direction: row-reverse;
  gap: 10px;
}

:deep(.v-switch .v-switch__track) {
  height: 28px;
  opacity: 1;
}

:deep(.v-switch .v-selection-control.v-selection-control--dirty .v-switch__track) {
  background-color: var(--blue-blue-hover);
}

:deep(.v-switch .v-selection-control:not(.v-selection-control--dirty) .v-switch__track) {
  background-color: #c7c7c7;
}

:deep(.v-switch .v-switch__thumb) {
  transform: none;
}
</style>
